import {Constants} from "@/api/constants";

/**
 * Generic state and adapter contract shared by every overview table variant (#1606 — request-internal,
 * request-external/bidder, and the three order variants). Pure types and value functions only — no React,
 * no network calls — so this module counts toward the Vitest coverage denominator without any rendering.
 *
 * `useOverviewTableState` (Phase 1.3) knows only the fields declared on `OverviewState` directly; the
 * `filters` field is an opaque `TFilter` it never inspects. Everything variant-specific — which four
 * endpoints back a variant, what "everything selected" means, how the persisted document and the search
 * parameters map onto the state — lives behind an `OverviewStateAdapter` (request-view-state.ts,
 * Phase 1.2; the order counterpart in Milestone 2).
 */

/** Mirrors `RequestSortDirection` / `OrderSortDirection` — both enums carry the same two values. */
export type SortDirection = "ASC" | "DESC";

/**
 * One overview table's state. `page`, `size`, `searchText`, `sortField` and `sortDirection` are the fields
 * the hook understands generically; `filters` carries everything variant-specific and is opaque to the
 * hook. `sortField` and `sortDirection` stay volatile in every variant (Phase 0, Entscheidung 1) — no
 * adapter ever persists them.
 */
export interface OverviewState<TFilter, TSortField extends string = string> {
    readonly page: number;
    readonly size: number;
    readonly searchText: string;
    readonly sortField?: TSortField;
    readonly sortDirection?: SortDirection;
    readonly filters: TFilter;
}

/**
 * One page of search results, generic over the row shape returned by a variant's search endpoint.
 * `totalUnreadMessages` sits here, not on a request- or order-specific type, because both overviews need it
 * (Milestone 5): the sum of unread messages over the whole filtered set, not the returned page, driving the
 * Info column header badge.
 */
export interface OverviewSearchResult<TRow> {
    readonly rows: TRow[];
    readonly page: number;
    readonly size: number;
    readonly totalElements: number;
    readonly totalPages: number;
    readonly totalUnreadMessages: number;
}

/**
 * Parses a comma-separated size-options constant (`Constants.REQUEST_SEARCH_SIZE_OPTIONS` /
 * `Constants.ORDER_SEARCH_SIZE_OPTIONS`, e.g. `"10,25,50"`) into an ascending list of unique integers
 * within `[sizeMin, sizeMax]`. Non-numeric entries and values outside the bounds are dropped silently —
 * the input is a hand-authored backend constant, not user input, so a malformed entry is a defect to
 * surface as a shrunken option list rather than a thrown error. Blank or missing input yields an empty
 * list.
 */
export function parseSizeOptions(raw: string | undefined, sizeMin: number, sizeMax: number): number[] {
    if (!raw || raw.trim() === "") {
        return [];
    }
    const parsed = raw
        .split(",")
        .map((entry) => Number.parseInt(entry.trim(), 10))
        .filter((value) => Number.isInteger(value) && value >= sizeMin && value <= sizeMax);
    return [...new Set(parsed)].sort((a, b) => a - b);
}

/** Page-size choices offered by the request-search page-size selector (Phase 1.3). */
export const REQUEST_SIZE_OPTIONS: number[] = parseSizeOptions(
    Constants.REQUEST_SEARCH_SIZE_OPTIONS,
    Constants.REQUEST_SEARCH_SIZE_MIN,
    Constants.REQUEST_SEARCH_SIZE_MAX
);

/** Page-size choices offered by the order-search page-size selector (Phase 1.3). */
export const ORDER_SIZE_OPTIONS: number[] = parseSizeOptions(
    Constants.ORDER_SEARCH_SIZE_OPTIONS,
    Constants.ORDER_SEARCH_SIZE_MIN,
    Constants.ORDER_SEARCH_SIZE_MAX
);

/**
 * Clamps a requested page index against the page count of the answer just received. `totalPages === 0`
 * (no hits at all) never triggers a clamp — page 0 is already the correct index there, and clamping it
 * again would be a no-op that only obscures the empty-result case.
 */
export function clampPage(requestedPage: number, totalPages: number): number {
    if (totalPages > 0 && requestedPage >= totalPages) {
        return 0;
    }
    return requestedPage;
}

/**
 * Builds the state `resetAll()` (Phase 1.3) applies: the caller-supplied `defaults` — already carrying
 * "everything selected" for the variant's status field(s) and empty value/date fields, per the adapter's
 * `defaults()` contract — replace `filters` wholesale, the free-text search is cleared, the page resets to
 * 0 and `size` resets to the caller-supplied `defaultSize` (the variant's own page-size default — see the
 * plan's "Standing constraints": an unreset `size` would show a value nobody has saved any more). The
 * volatile sort fields are left untouched — filtering and sorting stay separate concerns, a deliberate
 * choice, not an oversight. Deliberately ignorant of any concrete status list — only the adapter knows
 * which list means "everything selected" for a given variant (see the plan's "Standing constraints").
 */
export function resetToDefaults<TFilter, TSortField extends string = string>(
    state: OverviewState<TFilter, TSortField>,
    defaults: TFilter,
    defaultSize: number
): OverviewState<TFilter, TSortField> {
    return {
        ...state,
        filters: defaults,
        searchText: "",
        page: 0,
        size: defaultSize
    };
}

/** The two fields a sortable column header cycles through — see `sortMenuSelection` / `sortMenuChoice`. */
export interface SortState<TSortField extends string = string> {
    readonly sortField?: TSortField;
    readonly sortDirection?: SortDirection;
}

/**
 * The three entries a sortable column header's dropdown menu offers ("Aufsteigend", "Absteigend",
 * "Sortierung aufheben" — plan "Spaltenkopf-Filterung und Sortieranzeige", Standing constraint 1). Replaces
 * the three-click ring `nextSortState` used to implement: sorting is now a direct choice among three menu
 * entries, not a sequence of clicks on the same control.
 */
export type SortMenuEntry = "ASC" | "DESC" | "NONE";

/**
 * Which of the three menu entries is the currently active one for a given column — drives
 * `aria-checked` on the `menuitemradio` entries (Standing constraint 13). A column other than the
 * currently sorted one, or no sort at all, is always `"NONE"` — the ring's old "irrelevant previous
 * position" rule carries over unchanged.
 */
export function sortMenuSelection<TSortField extends string>(
    current: SortState<TSortField>,
    columnSortField: TSortField
): SortMenuEntry {
    if (current.sortField !== columnSortField) {
        return "NONE";
    }
    return current.sortDirection ?? "NONE";
}

/**
 * The `{sortField, sortDirection}` pair `setSort` (`useOverviewTableState`) expects for a clicked menu
 * entry. `"NONE"` clears BOTH fields — the neutral state is truthfully `{sortField: undefined,
 * sortDirection: undefined}`, not a leftover direction nobody chose (carried over from `nextSortState`).
 */
export function sortMenuChoice<TSortField extends string>(
    columnSortField: TSortField,
    entry: SortMenuEntry
): SortState<TSortField> {
    if (entry === "NONE") {
        return {sortField: undefined, sortDirection: undefined};
    }
    return {sortField: columnSortField, sortDirection: entry};
}

/**
 * Variant-specific knowledge the generic hook never has. `TDoc` is the persisted view-state DTO
 * (`RequestTableViewStateDTO` / `OrderTableViewStateDTO`), `TRow` the search-result row, `TFilter` the
 * opaque filter object carried inside `OverviewState`, `TSearchParams` the DTO sent to the variant's search
 * endpoint, `TSortField` the variant's own sort-field enum.
 *
 * `toDocument` and `toSearchParams` both receive the full `OverviewState` — "Zustand" and "flüchtige Werte"
 * in the plan's wording are the same object: `filters` and `size` are the persisted-relevant part,
 * `searchText`, `page`, `sortField` and `sortDirection` the volatile part `toDocument` ignores and
 * `toSearchParams` folds in.
 */
export interface OverviewStateAdapter<TDoc, TRow, TFilter, TSearchParams, TSortField extends string = string> {
    /** Resolves to `undefined` on the 204-empty-body case (see the plan's "Standing constraints"). */
    loadDocument(): Promise<TDoc | undefined>;
    saveDocument(doc: TDoc): Promise<void>;
    clearDocument(): Promise<void>;
    search(params: TSearchParams): Promise<OverviewSearchResult<TRow>>;
    /** "Everything selected" for this variant — the vocabulary only the adapter knows. */
    defaults(): TFilter;
    /**
     * The variant's page-size default (Phase 0, Entscheidung 2: `ORDER_SEARCH_SIZE_DEFAULT` for orders,
     * `REQUEST_SEARCH_SIZE_DEFAULT` for requests) — the only place that knows this number, so the generic
     * hook never hardcodes a page size of its own.
     */
    defaultSize(): number;
    /** Drops `searchText`, `page`, `sortField` and `sortDirection` — none of them is persisted. */
    toDocument(state: OverviewState<TFilter, TSortField>): TDoc;
    /** A missing document (`undefined`) and a document with no status field both fall back to `defaults`. */
    fromDocument(doc: TDoc | undefined, defaults: TFilter): TFilter;
    toSearchParams(state: OverviewState<TFilter, TSortField>): TSearchParams;
}
